import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { API_BASE, getProfileAssetUrl } from '../utils/api';
import { downloadAuthenticatedUrl } from '../utils/authenticatedBlobDownload';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from './ui/select';
import ClassFormDialog from './classes/ClassFormDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Plus, Search, Edit, Trash2, GraduationCap, Mail, Phone, Upload, FileSpreadsheet, Download, ChevronDown, UserPlus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';
import StudentFacilityToggles from './StudentFacilityToggles';
import StudentAdmissionSections from './students/StudentAdmissionSections';
import {
  createEmptyPendingUploads,
  uploadAllAdmissionAssets,
  validateAadharNumber,
} from '../utils/studentAdmissionUploads';
import { getSchoolFeatures, isSchoolFeatureEnabled } from '../utils/schoolFeatures';
import MobileInput from './ui/mobile-input';
import DateInput from './ui/date-input';
import { validateMobileFields, isValidMobile } from '../utils/phoneValidation';

/** @param {Record<string, unknown>|undefined|null} cls */
const classUsesSections = (cls) => {
  if (!cls) return true;
  if (cls.uses_sections === undefined || cls.uses_sections === null) return true;
  return Number(cls.uses_sections) === 1 || cls.uses_sections === true;
};

const Students = () => {
  const { user, canManageFeature } = useAuth();
  const { showBranchLabels, formatLabel } = useBranchLabels();
  const canManageStudents = canManageFeature('students');
  const canManageClasses = canManageFeature('classes');
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [filterSections, setFilterSections] = useState([]);
  const [listMeta, setListMeta] = useState({ total: 0, page: 1, limit: 50, pages: 0 });
  const [page, setPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [classCreateOpen, setClassCreateOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkClassId, setBulkClassId] = useState('');
  const [bulkSectionId, setBulkSectionId] = useState('');
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    admission_no: '',
    class_id: '',
    section_id: '',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    aadhar_number: '',
    guardian_aadhar_number: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_email: '',
    uses_library: false,
    uses_transport: false,
  });
  const schoolFeatures = getSchoolFeatures(user);
  const showStudentFacilities =
    isSchoolFeatureEnabled({ school_features: schoolFeatures }, 'library')
    || isSchoolFeatureEnabled({ school_features: schoolFeatures }, 'transport');
  const [parentLookup, setParentLookup] = useState({ loading: false, exists: false, account: null, checked: false });
  const [phoneErrors, setPhoneErrors] = useState({});
  const [submittingAdmission, setSubmittingAdmission] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(createEmptyPendingUploads);
  const lookupTimerRef = useRef(null);

  const describeEmailNotifications = (en) => {
    if (!en) return '';
    const parts = [];
    if (en.sent > 0) parts.push(`${en.sent} email(s) sent`);
    if (en.queued > 0) parts.push(`${en.queued} email(s) queued`);
    if (en.failed > 0) parts.push(`${en.failed} failed`);
    if (en.skipped?.length) {
      const reasons = en.skipped.map((s) => `${s.recipient}: ${s.reason}`).join('; ');
      parts.push(`skipped (${reasons})`);
    }
    return parts.join('. ');
  };

  const selectedClassForForm = useMemo(
    () => classes.find((c) => String(c.id) === String(formData.class_id)),
    [classes, formData.class_id],
  );
  const formClassUsesSections = useMemo(() => classUsesSections(selectedClassForForm), [selectedClassForForm]);

  const selectedBulkClass = useMemo(
    () => classes.find((c) => String(c.id) === String(bulkClassId)),
    [classes, bulkClassId],
  );
  const bulkClassUsesSections = useMemo(() => classUsesSections(selectedBulkClass), [selectedBulkClass]);

  const selectedClassForFilter = useMemo(
    () => classes.find((c) => String(c.id) === String(selectedClass)),
    [classes, selectedClass],
  );
  const filterClassUsesSections = useMemo(
    () => classUsesSections(selectedClassForFilter),
    [selectedClassForFilter],
  );

  const filtersReady = Boolean(selectedClass);

  useEffect(() => {
    fetchClasses();
  }, [user?.school_id]);

  useEffect(() => {
    if (!selectedClass) {
      setFilterSections([]);
      setSelectedSection('');
      setStudents([]);
      setListMeta({ total: 0, page: 1, limit: 50, pages: 0 });
      return;
    }
    if (!filterClassUsesSections) {
      setFilterSections([]);
      setSelectedSection('');
      return;
    }
    let cancelled = false;
    api
      .get(`classes/${selectedClass}/sections`)
      .then((res) => {
        if (!cancelled) setFilterSections(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setFilterSections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClass, filterClassUsesSections]);

  useEffect(() => {
    setSelectedSection('');
    setPage(1);
  }, [selectedClass]);

  useEffect(() => {
    if (!filtersReady) {
      setStudents([]);
      setLoading(false);
      return;
    }
    fetchStudents();
  }, [selectedClass, selectedSection, page, user?.school_id, filtersReady]);

  useEffect(() => {
    if (!filtersReady) return undefined;
    const timer = setTimeout(() => {
      setPage(1);
      fetchStudents(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchClasses();
      if (filtersReady) fetchStudents();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [selectedClass, selectedSection, filtersReady, user?.school_id]);

  useEffect(() => {
    if (!formData.class_id || !formClassUsesSections) {
      setSections([]);
      return;
    }
    let cancelled = false;
    api
      .get(`classes/${formData.class_id}/sections`)
      .then((res) => {
        if (!cancelled) setSections(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setSections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.class_id, formClassUsesSections]);

  const [bulkSections, setBulkSections] = useState([]);
  useEffect(() => {
    if (!bulkClassId || !bulkClassUsesSections) {
      setBulkSections([]);
      return;
    }
    let cancelled = false;
    api
      .get(`classes/${bulkClassId}/sections`)
      .then((res) => {
        if (!cancelled) setBulkSections(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setBulkSections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [bulkClassId, bulkClassUsesSections]);

  useEffect(() => {
    if (bulkClassId && !bulkClassUsesSections) {
      setBulkSectionId('');
    }
  }, [bulkClassId, bulkClassUsesSections]);

  useEffect(() => {
    if (editingStudent) {
      setParentLookup({ loading: false, exists: false, account: null, checked: false });
      return undefined;
    }

    const phone = formData.guardian_phone?.trim() || '';
    const email = formData.guardian_email?.trim() || '';
    if (!phone && !email) {
      setParentLookup({ loading: false, exists: false, account: null, checked: false });
      return undefined;
    }
    if (phone && !isValidMobile(phone)) {
      setParentLookup({ loading: false, exists: false, account: null, checked: false });
      return undefined;
    }

    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }

    lookupTimerRef.current = setTimeout(async () => {
      setParentLookup((prev) => ({ ...prev, loading: true }));
      try {
        const params = {
          ...(user?.school_id ? { school_id: user.school_id } : {}),
          ...(phone ? { mobile: phone } : {}),
          ...(email ? { email } : {}),
        };
        const res = await api.get('/fees/parent-accounts/lookup', { params });
        const data = res.data?.data;
        setParentLookup({
          loading: false,
          exists: !!data?.exists,
          account: data?.parent_account || null,
          checked: true,
        });
      } catch {
        setParentLookup({ loading: false, exists: false, account: null, checked: true });
      }
    }, 400);

    return () => {
      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, [formData.guardian_phone, formData.guardian_email, editingStudent, user?.school_id]);

  const fetchStudents = async (pageOverride) => {
    if (!filtersReady) return;
    setLoading(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const currentPage = pageOverride ?? page;
      const params = {
        class_id: selectedClass,
        page: currentPage,
        limit: listMeta.limit || 50,
        ...(filterClassUsesSections && selectedSection ? { section_id: selectedSection } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      };
      const response = await api.get('/students', { params });
      const payload = response.data.data;
      if (payload && Array.isArray(payload.students)) {
        setStudents(payload.students);
        setListMeta(payload.meta || { total: 0, page: currentPage, limit: 50, pages: 0 });
      } else {
        setStudents(Array.isArray(payload) ? payload : []);
        setListMeta({ total: (Array.isArray(payload) ? payload.length : 0), page: 1, limit: 50, pages: 1 });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch students', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const response = await api.get('/classes', { params });
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const handleClassCreated = async (newClassId) => {
    const classIdStr = String(newClassId);
    await fetchClasses();
    setFormData((prev) => ({ ...prev, class_id: classIdStr, section_id: '' }));
    try {
      const res = await api.get(`classes/${newClassId}/sections`);
      const list = res.data.data || [];
      setSections(list);
      if (list.length === 1) {
        setFormData((prev) => ({ ...prev, class_id: classIdStr, section_id: String(list[0].id) }));
      }
    } catch {
      setSections([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateMobileFields({
      phone: formData.phone,
      guardian_phone: formData.guardian_phone,
    });
    if (Object.keys(errors).length) {
      setPhoneErrors(errors);
      toast({ title: 'Invalid mobile number', description: Object.values(errors)[0], variant: 'destructive' });
      return;
    }
    setPhoneErrors({});
    if (formClassUsesSections && !formData.section_id) {
      toast({ title: 'Section required', description: 'Select a section for this class.', variant: 'destructive' });
      return;
    }
    const aadharErr = validateAadharNumber(formData.aadhar_number, 'Student Aadhar')
      || validateAadharNumber(formData.guardian_aadhar_number, 'Guardian Aadhar');
    if (aadharErr) {
      toast({ title: 'Invalid Aadhar', description: aadharErr, variant: 'destructive' });
      return;
    }
    const payload = {
      ...formData,
      phone: formData.phone || null,
      guardian_phone: formData.guardian_phone || null,
      aadhar_number: formData.aadhar_number?.trim() || null,
      guardian_aadhar_number: formData.guardian_aadhar_number?.trim() || null,
      address: formData.address?.trim() || null,
      city: formData.city?.trim() || null,
      state: formData.state?.trim() || null,
      pincode: formData.pincode?.trim() || null,
      class_id: formData.class_id ? parseInt(formData.class_id, 10) : null,
      section_id: formClassUsesSections && formData.section_id ? parseInt(formData.section_id, 10) : null,
      uses_library: formData.uses_library ? 1 : 0,
      uses_transport: formData.uses_transport ? 1 : 0,
    };
    if (!editingStudent) setSubmittingAdmission(true);
    try {
      if (editingStudent) {
        await api.put(`/students/${editingStudent.id}`, payload);
        toast({ title: 'Success', description: 'Student updated successfully' });
      } else {
        const res = await api.post('/students', payload);
        const pdata = res.data?.data;
        let desc = res.data?.message || 'Student created successfully.';
        if (pdata?.parent_linked_existing) {
          desc = 'Student created and linked to existing parent fee account.';
        } else if (pdata?.parent_account_id) {
          desc = 'Student created and linked to new parent fee account.';
        }
        const emailNote = describeEmailNotifications(pdata?.email_notifications);
        if (emailNote) desc += ` ${emailNote}`;
        const en = pdata?.email_notifications;
        const allSkipped = en && en.queued === 0 && (en.skipped?.length || 0) > 0;
        const newStudentId = pdata?.id || pdata?.student_id;
        let uploadErrors = [];
        if (newStudentId) {
          const uploadResult = await uploadAllAdmissionAssets(newStudentId, pendingUploads);
          uploadErrors = uploadResult.errors;
        }
        if (uploadErrors.length > 0) {
          toast({
            title: 'Student created — some uploads failed',
            description: `${desc} ${uploadErrors.map((e) => `${e.field}: ${e.message}`).join('; ')}`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: allSkipped ? 'Admission saved' : 'Success',
            description: desc,
            variant: allSkipped ? 'destructive' : 'default',
          });
        }
      }
      setIsDialogOpen(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Operation failed',
        variant: 'destructive' 
      });
    } finally {
      setSubmittingAdmission(false);
    }
  };

  const handleEdit = (student) => {
    navigate(`/students/${student.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await api.delete(`/students/${id}`);
        toast({ title: 'Success', description: 'Student deleted successfully' });
        fetchStudents();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to delete student', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', admission_no: '',
      class_id: '', section_id: '', date_of_birth: '', gender: '',
      address: '', city: '', state: '', pincode: '',
      aadhar_number: '', guardian_aadhar_number: '',
      guardian_name: '', guardian_phone: '', guardian_email: '',
      uses_library: false,
      uses_transport: false,
    });
    setPendingUploads(createEmptyPendingUploads());
    setParentLookup({ loading: false, exists: false, account: null, checked: false });
    setSections([]);
    setEditingStudent(null);
  };

  const downloadSample = async (format) => {
    try {
      await downloadAuthenticatedUrl(
        `${API_BASE}/api/students/sample?format=${format}`,
        `students_sample.${format}`,
      );
      toast({ title: 'Download started', description: `Sample ${format.toUpperCase()} file downloaded.` });
    } catch (err) {
      toast({ title: 'Download failed', description: err?.message || 'Could not download sample.', variant: 'destructive' });
    }
  };

  const BULK_MAX_BYTES = 10 * 1024 * 1024;

  const validateBulkFile = (file) => {
    if (!file) return 'Please select a CSV or Excel file.';
    const name = file.name.toLowerCase();
    if (!/\.(csv|xlsx|xls)$/.test(name)) {
      return 'Allowed formats: CSV, XLSX, XLS';
    }
    if (file.size > BULK_MAX_BYTES) {
      return 'File too large. Maximum size is 10 MB.';
    }
    return null;
  };

  const handleBulkUpload = async () => {
    const fileError = validateBulkFile(bulkFile);
    if (fileError) {
      toast({ title: 'Invalid file', description: fileError, variant: 'destructive' });
      return;
    }
    if (!bulkClassId) {
      toast({ title: 'Class required', description: 'Please select a class for the students.', variant: 'destructive' });
      return;
    }
    if (bulkClassUsesSections && !bulkSectionId) {
      toast({ title: 'Section required', description: 'Please select a section for this class.', variant: 'destructive' });
      return;
    }
    setBulkUploading(true);
    setBulkUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('class_id', bulkClassId);
      if (bulkSectionId) {
        formData.append('section_id', bulkSectionId);
      }
      const res = await api.post('students/bulk', formData);
      const data = res.data?.data || {};
      const created = data.created ?? 0;
      const failed = data.failed || [];
      setBulkUploadResult({ created, failed, total_rows: data.total_rows ?? 0 });
      if (filtersReady) fetchStudents();
      if (failed.length === 0) {
        toast({ title: 'Success', description: `${created} student(s) added successfully.` });
        setBulkFile(null);
        setBulkClassId('');
        setBulkSectionId('');
        setBulkUploadResult(null);
        setBulkDialogOpen(false);
        if (document.getElementById('bulk-file-input')) document.getElementById('bulk-file-input').value = '';
      } else {
        toast({
          title: 'Bulk upload completed with errors',
          description: `${created} student(s) added. ${failed.length} row(s) failed — see details below.`,
          variant: created === 0 ? 'destructive' : 'default',
        });
      }
    } catch (err) {
      toast({
        title: 'Bulk upload failed',
        description: err.response?.data?.message || err?.message || 'Could not upload file.',
        variant: 'destructive',
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const displayedStudents = students;

  return (
    <div className="space-y-6" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Students</h1>
          <p className="text-muted-foreground">Manage student records</p>
        </div>
        {canManageStudents && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-student-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Student
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 border-border text-foreground">
            <DropdownMenuItem
              className="cursor-pointer focus:bg-muted focus:text-foreground"
              onSelect={(e) => { e.preventDefault(); setIsDialogOpen(true); resetForm(); }}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Single upload
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer focus:bg-muted focus:text-foreground"
              onSelect={(e) => { e.preventDefault(); setBulkDialogOpen(true); }}
            >
              <Upload className="w-4 h-4 mr-2" />
              <span className="flex-1">Bulk upload</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
        {canManageStudents && (
        <>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="border-border text-foreground max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="bg-muted border-border"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-muted border-border"
                    disabled={!!editingStudent}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <MobileInput
                    value={formData.phone}
                    onChange={(v) => {
                      setFormData({ ...formData, phone: v });
                      setPhoneErrors((prev) => ({ ...prev, phone: undefined }));
                    }}
                    error={phoneErrors.phone}
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Admission No</Label>
                  <Input
                    value={formData.admission_no}
                    onChange={(e) => setFormData({...formData, admission_no: e.target.value})}
                    className="bg-muted border-border"
                    disabled={!!editingStudent}
                    placeholder="Auto-generated if empty"
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <DateInput
                    value={formData.date_of_birth}
                    onChange={(v) => setFormData({...formData, date_of_birth: v})}
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div className={`grid gap-4 ${formClassUsesSections ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <Label>Class</Label>
                  <Select
                    value={formData.class_id}
                    onValueChange={(v) => {
                      if (v === '__create_class__') {
                        setClassCreateOpen(true);
                        return;
                      }
                      setFormData({ ...formData, class_id: v, section_id: '' });
                    }}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {classes.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>{formatLabel(c.name, c)}</SelectItem>
                      ))}
                      {canManageClasses && (
                        <>
                          <SelectSeparator />
                          <SelectItem value="__create_class__" className="text-blue-600 focus:text-blue-600">
                            <span className="flex items-center gap-2">
                              <Plus className="w-4 h-4" />
                              Create class…
                            </span>
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {formClassUsesSections && (
                <div>
                  <Label>Section *</Label>
                  <Select
                    value={formData.section_id}
                    onValueChange={(v) => setFormData({ ...formData, section_id: v })}
                    disabled={!formData.class_id}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder={formData.class_id ? 'Select section' : 'Select class first'} />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {sections.map((s) => (
                        <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <StudentAdmissionSections
                values={formData}
                onChange={setFormData}
                pendingUploads={pendingUploads}
                onPendingUploadsChange={setPendingUploads}
                showAddress
                showStudentSection
                showGuardianSection
              />
              <div className="border-t border-border pt-4 space-y-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Parent / Guardian</p>
                  <p className="text-xs text-muted-foreground">Used to create or link a parent fee account.</p>
                </div>
                <div>
                  <Label>Guardian name</Label>
                  <Input
                    value={formData.guardian_name}
                    onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                    className="bg-muted border-border"
                    placeholder="Full name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Guardian phone</Label>
                    <MobileInput
                      value={formData.guardian_phone}
                      onChange={(v) => {
                        setFormData({ ...formData, guardian_phone: v });
                        setPhoneErrors((prev) => ({ ...prev, guardian_phone: undefined }));
                      }}
                      error={phoneErrors.guardian_phone}
                      className="bg-muted border-border"
                      placeholder="Contact number"
                    />
                  </div>
                  <div>
                    <Label>Guardian email</Label>
                    <Input
                      type="email"
                      value={formData.guardian_email}
                      onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                      className="bg-muted border-border"
                      placeholder="Email address"
                    />
                    {!editingStudent && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Guardian login credentials are sent to this address when admission is created.
                      </p>
                    )}
                  </div>
                </div>
                {!editingStudent && (
                  <p className="text-xs text-muted-foreground">
                    After save, credential emails go to the guardian (priority) and student when each has a valid address.
                  </p>
                )}
                {!editingStudent && (formData.guardian_phone?.trim() || formData.guardian_email?.trim()) && (
                  <div className={`text-sm rounded-md px-3 py-2 border ${
                    parentLookup.loading
                      ? 'border-border text-muted-foreground bg-muted/50'
                      : parentLookup.exists
                        ? 'border-blue-600/50 bg-blue-600/10 text-blue-400'
                        : parentLookup.checked
                          ? 'border-green-600/50 bg-green-600/10 text-green-400'
                          : 'border-border text-muted-foreground bg-muted/50'
                  }`}>
                    {parentLookup.loading && 'Checking parent account…'}
                    {!parentLookup.loading && parentLookup.exists && parentLookup.account && (
                      <>Existing parent account found: <strong>{parentLookup.account.name}</strong>. Student will link on save.</>
                    )}
                    {!parentLookup.loading && parentLookup.checked && !parentLookup.exists && (
                      <>No matching account. New parent fee account will be created on save.</>
                    )}
                  </div>
                )}
              </div>
              {showStudentFacilities && (
              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground mb-3">Facilities</p>
                <StudentFacilityToggles
                  uses_library={formData.uses_library}
                  uses_transport={formData.uses_transport}
                  schoolFeatures={schoolFeatures}
                  onChange={(field, checked) => setFormData((prev) => ({ ...prev, [field]: checked }))}
                />
              </div>
              )}
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={submittingAdmission || (formClassUsesSections && !formData.section_id)}
              >
                {submittingAdmission ? 'Creating admission…' : editingStudent ? 'Update Student' : 'Create Student'}
              </Button>
            </form>
            {canManageClasses && (
              <ClassFormDialog
                open={classCreateOpen}
                onOpenChange={setClassCreateOpen}
                editingClass={null}
                academicYearId={getSelectedAcademicYearId()}
                onSuccess={handleClassCreated}
              />
            )}
          </DialogContent>
        </Dialog>
        {/* Bulk upload dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={(open) => { setBulkDialogOpen(open); if (!open) { setBulkFile(null); setBulkClassId(''); setBulkSectionId(''); setBulkUploadResult(null); } }}>
          <DialogContent className="border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" /> Bulk upload students
              </DialogTitle>
              <p className="text-muted-foreground text-sm font-normal">
                Upload a CSV or Excel file with student data. Select the class below — all students will be added to that class. Only "Student Name" column is required.
              </p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-foreground mb-1 block">Class *</Label>
                <Select
                  value={bulkClassId}
                  onValueChange={(v) => {
                    setBulkClassId(v);
                    setBulkSectionId('');
                  }}
                >
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select class for all students" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {classes.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{formatLabel(c.name, c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {bulkClassUsesSections && (
              <div>
                <Label className="text-sm text-foreground mb-1 block">Section *</Label>
                <Select value={bulkSectionId} onValueChange={setBulkSectionId} disabled={!bulkClassId}>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder={bulkClassId ? 'Default section for all rows' : 'Select class first'} />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {bulkSections.map((s) => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Used when file has no section_id column.</p>
              </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <input
                  id="bulk-file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setBulkUploadResult(null);
                    if (file) {
                      const err = validateBulkFile(file);
                      if (err) {
                        toast({ title: 'Invalid file', description: err, variant: 'destructive' });
                        e.target.value = '';
                        setBulkFile(null);
                        return;
                      }
                    }
                    setBulkFile(file);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-border text-foreground hover:bg-muted"
                  onClick={() => document.getElementById('bulk-file-input')?.click()}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Choose file
                </Button>
                {bulkFile && <span className="text-sm text-muted-foreground">{bulkFile.name}</span>}
              </div>
              <Button
                type="button"
                onClick={handleBulkUpload}
                disabled={!bulkFile || !bulkClassId || bulkUploading || (bulkClassUsesSections && !bulkSectionId)}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {bulkUploading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2" />
                    Uploading…
                  </>
                ) : (
                  'Upload Students'
                )}
              </Button>
              {bulkUploadResult?.failed?.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-muted/40 text-sm font-medium text-foreground">
                    Failed rows ({bulkUploadResult.failed.length})
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="p-2 w-14">Row</th>
                          <th className="p-2">Name</th>
                          <th className="p-2">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkUploadResult.failed.map((f, idx) => (
                          <tr key={`${f.row}-${idx}`} className="border-b border-border/50">
                            <td className="p-2">{f.row}</td>
                            <td className="p-2">{f.name || '—'}</td>
                            <td className="p-2 text-red-400">{f.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <p className="text-muted-foreground text-sm flex items-center gap-2 flex-wrap">
                <Download className="w-4 h-4" />
                Download sample:
                <button
                  type="button"
                  onClick={() => downloadSample('csv')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  CSV
                </button>
                <span className="text-muted-foreground">|</span>
                <button
                  type="button"
                  onClick={() => downloadSample('xlsx')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Excel (.xlsx)
                </button>
              </p>
            </div>
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>

      {/* Filters */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-muted border-border text-foreground"
              />
            </div>
            <Select value={selectedClass || ''} onValueChange={(v) => setSelectedClass(v)}>
              <SelectTrigger className="w-48 bg-muted border-border text-foreground">
                <SelectValue placeholder="Select class" />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border">
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>{formatLabel(c.name, c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedClass && filterClassUsesSections && (
              <Select value={selectedSection || '__all__'} onValueChange={(v) => { setSelectedSection(v === '__all__' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-48 bg-muted border-border text-foreground">
                  <SelectValue placeholder="All sections" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="__all__">All sections</SelectItem>
                  {filterSections.map((sec) => (
                    <SelectItem key={sec.id} value={sec.id.toString()}>{sec.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedClass && !filterClassUsesSections && (
              <div className="w-48 flex items-center px-3 py-2 text-sm text-muted-foreground border border-border rounded-md bg-muted/50">
                No sections
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      {!filtersReady ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Select a class to view students.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : displayedStudents.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No students found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {listMeta.pages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing page {listMeta.page} of {listMeta.pages} ({listMeta.total} student{listMeta.total !== 1 ? 's' : ''})
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= listMeta.pages}
                  onClick={() => setPage((p) => Math.min(listMeta.pages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          <div className="grid gap-4">
          {displayedStudents.map((student) => (
            <Card key={student.id} className="border-border hover:border-border transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/students/${student.id}`}
                    className="flex items-center gap-4 flex-1 min-w-0"
                  >
                    <Avatar className="w-12 h-12 shrink-0">
                      <AvatarImage 
                        src={getProfileAssetUrl(student.avatar)} 
                        alt={`${student.first_name} ${student.last_name}`}
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-blue-500/20 text-blue-400 font-semibold">
                        {student.first_name?.[0]?.toUpperCase()}{student.last_name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground hover:text-blue-300 transition-colors flex items-center gap-2 flex-wrap">
                        {student.first_name} {student.last_name}
                        <BranchLabel name={student.branch_name} show={showBranchLabels} />
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {student.admission_no && !student.admission_no.startsWith('ADM-') ? student.admission_no : ''}
                        {student.admission_no && !student.admission_no.startsWith('ADM-') ? ' • ' : ''}
                        {student.class_name || 'No class assigned'}
                        {student.section_name ? ` • ${student.section_name}` : ''}
                      </p>
                      <div className="flex items-center gap-4 mt-1">
                        {student.email && !student.email.endsWith('@noemail.local') && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {student.email}
                        </span>
                        )}
                        {student.phone && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {student.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  {canManageStudents && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.preventDefault(); handleEdit(student); }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.preventDefault(); handleDelete(student.id); }}
                      className="text-muted-foreground hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
